import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import { ApiHook } from "../../../hooks/apiHook";

const NetworkSalesSection = ({ startDate, endDate, legId }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading, isFetching, isError } = ApiHook.CallNetworkSales(
    startDate,
    endDate,
    legId,
    page,
    pageSize
  );

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" /> {t("loading")}
      </div>
    );
  }

  if (isError || !data?.data) {
    return <div className="alert alert-warning">{t("errorLoadingData")}</div>;
  }

  const { sales, pagination, totals } = data.data;
  const totalPages = Math.ceil(pagination.total / pagination.pageSize) || 1;

  const formatNum = (n) => {
    if (n == null || isNaN(n)) return "0";
    return Number(n).toLocaleString();
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className="row mb-3">
        <div className="col-md-6 mb-2">
          <div className="card shadow-sm border-0">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("groupSales")}</small>
              <h5 className="mb-0" style={{ color: "#4e73df", fontWeight: "bold" }}>
                {formatNum(totals.totalGroupSales)}
              </h5>
            </div>
          </div>
        </div>
        <div className="col-md-6 mb-2">
          <div className="card shadow-sm border-0">
            <div className="card-body text-center py-2">
              <small className="text-muted">{t("totalRecords")}</small>
              <h5 className="mb-0" style={{ fontWeight: "bold" }}>
                {pagination.total}
              </h5>
            </div>
          </div>
        </div>
      </div>

      {isFetching && !isLoading && (
        <div className="text-center mb-2">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {/* Sales Table */}
      {sales && sales.length > 0 ? (
        <div className="card shadow-sm border-0">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0" style={{ fontSize: "12px" }}>
                <thead className="bg-light">
                  <tr>
                    <th>{t("date")}</th>
                    <th className="text-end">{t("saleValue")}</th>
                    <th>{t("sponsor")}</th>
                    <th className="text-end">{t("sponsorDirectSales")}</th>
                    <th className="text-end">{t("sponsorGroupSales")}</th>
                    <th>{t("legLabel")}</th>
                    <th>{t("saleType")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((row) => (
                    <tr key={row.saleId}>
                      <td className="text-nowrap">{formatDate(row.date)}</td>
                      <td className="text-end">
                        <strong style={{ color: "#1cc88a" }}>{formatNum(row.saleValue)}</strong>
                      </td>
                      <td>
                        {row.sponsor ? (
                          <>
                            <strong>{row.sponsor.username}</strong>
                            {row.sponsor.fullName && (
                              <div className="text-muted" style={{ fontSize: "11px" }}>
                                {row.sponsor.fullName}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-end">
                        {row.sponsor ? formatNum(row.sponsor.directSales) : "-"}
                      </td>
                      <td className="text-end">
                        {row.sponsor ? formatNum(row.sponsor.groupSales) : "-"}
                      </td>
                      <td>
                        {row.legRoot ? (
                          <span className="badge bg-light text-dark" style={{ fontSize: "11px" }}>
                            {t("legLabel")} {row.legRoot.legPosition}: {row.legRoot.username}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {row.saleType && (
                          <span
                            className="badge"
                            style={{
                              backgroundColor: row.saleType === "migration" ? "#858796" : "#36b9cc",
                              color: "#fff",
                              fontSize: "10px",
                            }}
                          >
                            {row.saleType}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="alert alert-info">{t("noDataAvailable")}</div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            {t("page")} {page} {t("of")} {totalPages} ({pagination.total} {t("totalRecords")})
          </small>
          <div>
            <button
              className="btn btn-sm btn-outline-secondary me-2"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              {t("previous")}
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              {t("next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkSalesSection;
